import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  SimpleGrid,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { ArrowBackIcon, DeleteIcon } from "@chakra-ui/icons";
import { FiPlay, FiCheckCircle, FiRefreshCw } from "react-icons/fi";
import { sprintService } from "../services/edosService";
import api from "../services/api";

const STATUS_COLORS = {
  planned: "gray",
  active: "green",
  completed: "teal",
  cancelled: "red",
};

const normalizeTicketRows = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.tickets)) return payload.data.tickets;
  if (Array.isArray(payload?.tickets)) return payload.tickets;
  return [];
};

export default function SprintDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const [sprint, setSprint] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sprintRes, analyticsRes] = await Promise.all([
        sprintService.getById(id),
        sprintService.analytics(id),
      ]);
      setSprint(sprintRes?.data?.data || sprintRes?.data);
      setAnalytics(analyticsRes?.data?.data || analyticsRes?.data || null);

      const allRows = [];
      let page = 1;
      let totalPages = 1;
      do {
        const res = await api.get("/api/tickets", {
          params: {
            page,
            limit: 100,
            sprintId: id,
            _viewMode: "all",
            sortBy: "createdAt",
            sortOrder: "desc",
          },
        });
        const payload = res?.data;
        allRows.push(...normalizeTicketRows(payload));
        totalPages = Number(
          payload?.totalPages || payload?.data?.totalPages || 1,
        );
        page += 1;
      } while (page <= totalPages && page <= 50);
      setTickets(allRows);
    } catch {
      toast({ title: "Failed to load sprint details", status: "error" });
      setSprint(null);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStart = async () => {
    setActionLoading(true);
    try {
      await sprintService.start(id);
      toast({ title: "Sprint started", status: "success" });
      load();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to start sprint",
        status: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!window.confirm("Mark this sprint as completed?")) return;
    setActionLoading(true);
    try {
      await sprintService.complete(id);
      toast({ title: "Sprint completed", status: "success" });
      load();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to complete sprint",
        status: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this sprint?")) return;
    setActionLoading(true);
    try {
      await sprintService.remove(id);
      toast({ title: "Sprint deleted", status: "success" });
      navigate("/edos/sprints");
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to delete sprint",
        status: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Spinner size="xl" color="purple.500" />
      </Flex>
    );
  }

  if (!sprint) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          Sprint not found.
        </Alert>
        <Button
          mt={4}
          leftIcon={<ArrowBackIcon />}
          onClick={() => navigate("/edos/sprints")}
        >
          Back to Sprints
        </Button>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={3}>
        <HStack>
          <Button
            leftIcon={<ArrowBackIcon />}
            variant="ghost"
            onClick={() => navigate("/edos/sprints")}
          >
            Sprints
          </Button>
          <Text fontSize="2xl" fontWeight="bold">
            {sprint.sprintName}
          </Text>
          <Badge colorScheme={STATUS_COLORS[sprint.status] || "gray"}>
            {sprint.status}
          </Badge>
        </HStack>
        <HStack>
          <Button leftIcon={<FiRefreshCw />} variant="outline" onClick={load}>
            Refresh
          </Button>
          {sprint.status === "planned" && (
            <Button
              leftIcon={<FiPlay />}
              colorScheme="green"
              isLoading={actionLoading}
              onClick={handleStart}
            >
              Start
            </Button>
          )}
          {sprint.status === "active" && (
            <Button
              leftIcon={<FiCheckCircle />}
              colorScheme="teal"
              isLoading={actionLoading}
              onClick={handleComplete}
            >
              Complete
            </Button>
          )}
          <Button
            leftIcon={<DeleteIcon />}
            colorScheme="red"
            variant="outline"
            isLoading={actionLoading}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </HStack>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <Box
          p={4}
          bg={bg}
          border="1px"
          borderColor={borderColor}
          borderRadius="md"
        >
          <Text fontSize="xs" color="gray.500">
            Goal
          </Text>
          <Text fontWeight="semibold">{sprint.sprintGoal || "-"}</Text>
        </Box>
        <Box
          p={4}
          bg={bg}
          border="1px"
          borderColor={borderColor}
          borderRadius="md"
        >
          <Text fontSize="xs" color="gray.500">
            Date Range
          </Text>
          <Text fontWeight="semibold">
            {sprint.startDate
              ? new Date(sprint.startDate).toLocaleDateString()
              : "-"}{" "}
            -{" "}
            {sprint.endDate
              ? new Date(sprint.endDate).toLocaleDateString()
              : "-"}
          </Text>
        </Box>
        <Box
          p={4}
          bg={bg}
          border="1px"
          borderColor={borderColor}
          borderRadius="md"
        >
          <Text fontSize="xs" color="gray.500">
            Points
          </Text>
          <Text fontWeight="semibold">
            {sprint.completedStoryPoints ?? 0}/{sprint.plannedStoryPoints ?? 0}
          </Text>
        </Box>
      </SimpleGrid>

      {analytics && (
        <Box
          mb={6}
          p={4}
          bg={bg}
          border="1px"
          borderColor={borderColor}
          borderRadius="md"
        >
          <Text fontWeight="bold" mb={3}>
            Analytics
          </Text>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
            {Object.entries(analytics)
              .filter(([, v]) => typeof v !== "object")
              .map(([k, v]) => (
                <Box key={k} p={3} bg={headerBg} borderRadius="md">
                  <Text fontSize="xs" color="gray.500">
                    {k.replace(/([A-Z])/g, " $1")}
                  </Text>
                  <Text fontWeight="semibold">{String(v)}</Text>
                </Box>
              ))}
          </SimpleGrid>
        </Box>
      )}

      <Box
        bg={bg}
        border="1px"
        borderColor={borderColor}
        borderRadius="md"
        overflow="hidden"
      >
        <Box
          px={4}
          py={3}
          bg={headerBg}
          borderBottom="1px"
          borderColor={borderColor}
        >
          <Text fontWeight="bold">Linked Tickets ({tickets.length})</Text>
        </Box>
        {tickets.length === 0 ? (
          <Alert status="info" m={4}>
            <AlertIcon />
            No tickets linked to this sprint.
          </Alert>
        ) : (
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Ticket</Th>
                <Th>Title</Th>
                <Th>Status</Th>
                <Th>Priority</Th>
              </Tr>
            </Thead>
            <Tbody>
              {tickets.map((ticket) => (
                <Tr
                  key={ticket._id}
                  _hover={{ bg: headerBg, cursor: "pointer" }}
                  onClick={() => navigate(`/Ticketing/${ticket._id}`)}
                >
                  <Td>{ticket.ticketCode || ticket.ticketId || "-"}</Td>
                  <Td>
                    <Text noOfLines={1}>{ticket.title || "-"}</Text>
                  </Td>
                  <Td>
                    <Badge colorScheme="gray">
                      {String(ticket.workflowState || "open").replace(
                        /_/g,
                        " ",
                      )}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme="orange">{ticket.priority || "-"}</Badge>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </Box>
  );
}
